import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { cortexApi } from '@/api/cortex.api';
import { formatTimestamp } from '@/utils/utils';

type TabId = 'overview' | 'groups';

export default function SkillGroupClassDetailPage() {
  const navigate = useNavigate();
  const { uid: id = '' } = useParams<{ uid: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const {
    data: classData,
    isLoading: classLoading,
    isError: classError,
    refetch: refetchClass,
  } = useQuery({
    queryKey: ['skillgroup-class-detail', id],
    enabled: !!id,
    queryFn: () => cortexApi.getSkillGroupClass(id),
  });

  const {
    data: groupsData,
    isLoading: groupsLoading,
    isError: groupsError,
    refetch: refetchGroups,
  } = useQuery({
    queryKey: ['skillgroups-under-class', id],
    enabled: !!id,
    queryFn: () =>
      cortexApi.listSkillGroups(
        create(ListRequestSchema, {
          page: 1,
          pageSize: 100,
        })
      ),
  });

  const relatedGroups = useMemo(
    () => (groupsData?.data || []).filter((item) => item.classId === id),
    [groupsData, id]
  );

  if (classLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (classError || !classData) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-10 text-center space-y-3">
            <p className="text-sm text-destructive">技能组模版加载失败</p>
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" onClick={() => navigate('/plugins/skills')}>
                返回技能组
              </Button>
              <Button onClick={() => refetchClass()}>重试</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{classData.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">技能组模版详情与关联技能组列表。</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/plugins/skills')}>
            返回技能组
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/plugins/skills?tab=group&create=1&classId=${classData.id}`)}
          >
            新建技能组
          </Button>
          <Button onClick={() => (activeTab === 'overview' ? refetchClass() : refetchGroups())}>
            刷新
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">技能组模版信息</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground mb-1">ID</div>
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{classData.id}</code>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">描述</div>
            <div>{classData.description || '-'}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">默认技能数</div>
            <div>{Object.keys(classData.template?.skills || {}).length}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">创建时间</div>
            <div>{formatTimestamp(classData.createdAt)}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">更新时间</div>
            <div>{formatTimestamp(classData.updatedAt)}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <Tabs
          tabs={[
            { id: 'overview', label: '默认技能' },
            { id: 'groups', label: '技能组实例' },
          ]}
          activeTab={activeTab}
          onChange={(tab) => setActiveTab(tab as TabId)}
        />
        <CardContent className="pt-5">
          {activeTab === 'overview' ? (
            Object.keys(classData.template?.skills || {}).length === 0 ? (
              <p className="text-sm text-muted-foreground">当前类未配置默认技能。</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(classData.template?.skills || {}).map(([name, version]) => (
                  <div
                    key={name}
                    className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                  >
                    <span className="font-medium">{name}</span>
                    <Badge variant="outline">{version || 'latest'}</Badge>
                  </div>
                ))}
              </div>
            )
          ) : groupsLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : groupsError ? (
            <div className="space-y-3">
              <p className="text-sm text-destructive">加载技能组失败</p>
              <Button size="sm" variant="outline" onClick={() => refetchGroups()}>
                重试
              </Button>
            </div>
          ) : relatedGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground">该技能组模版下暂无技能组实例。</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名称</TableHead>
                  <TableHead>技能数</TableHead>
                  <TableHead>更新时间</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {relatedGroups.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer hover:bg-muted/20"
                    onClick={() => navigate(`/plugins/skills/groups/${item.id}`)}
                  >
                    <TableCell>
                      <button
                        type="button"
                        className="font-medium text-primary hover:text-primary/80 text-left"
                        onClick={() => navigate(`/plugins/skills/groups/${item.id}`)}
                      >
                        {item.name}
                      </button>
                      <div className="text-xs text-muted-foreground">ID: {item.id}</div>
                    </TableCell>
                    <TableCell>{Object.keys(item.skills || {}).length}</TableCell>
                    <TableCell>{formatTimestamp(item.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
